/** Set this in `.env` (restart `npm start` after changing). */
export const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "YOUR_GOOGLE_MAPS_API_KEY_HERE";

export const GOOGLE_MAPS_API_KEY_PLACEHOLDER = "YOUR_GOOGLE_MAPS_API_KEY_HERE";

export function hasGoogleMapsApiKey() {
  const key = String(GOOGLE_MAPS_API_KEY || "").trim();
  return Boolean(key) && key !== GOOGLE_MAPS_API_KEY_PLACEHOLDER;
}

let loadPromise = null;

/**
 * Loads the Google Maps JavaScript API once.
 * @param {string[]} [libraries]
 * @returns {Promise<typeof google.maps>}
 */
export function loadGoogleMaps(libraries = []) {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Google Maps requires a browser"));
  }

  if (window.google?.maps) {
    return Promise.resolve(window.google.maps);
  }

  if (!hasGoogleMapsApiKey()) {
    return Promise.reject(
      new Error(
        "Missing Google Maps API key. Set REACT_APP_GOOGLE_MAPS_API_KEY in .env (see .env.example)."
      )
    );
  }

  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    const finish = () => {
      if (window.google?.maps) resolve(window.google.maps);
      else reject(new Error("Google Maps failed to initialize"));
    };

    const existing = document.querySelector("script[data-google-maps]");
    if (existing) {
      if (window.google?.maps) {
        finish();
        return;
      }
      const started = Date.now();
      const t = setInterval(() => {
        if (window.google?.maps) {
          clearInterval(t);
          finish();
        } else if (Date.now() - started > 15000) {
          clearInterval(t);
          loadPromise = null;
          reject(new Error("Google Maps load timeout"));
        }
      }, 50);
      return;
    }

    const params = new URLSearchParams({
      key: GOOGLE_MAPS_API_KEY,
      v: "weekly",
    });
    if (libraries.length) params.set("libraries", libraries.join(","));

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
    script.async = true;
    script.defer = true;
    script.setAttribute("data-google-maps", "true");
    script.onload = finish;
    script.onerror = () => {
      loadPromise = null;
      reject(new Error("Failed to load Google Maps script"));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}

/** west,south,east,north — matches existing GIS bbox API */
export function buildBboxFromGoogleBounds(bounds) {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  return `${sw.lng()},${sw.lat()},${ne.lng()},${ne.lat()}`;
}

export function plotDevStatusColor(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("complete") || s.includes("fully")) return "#16a34a";
  if (s.includes("incomplete")) return "#f59e0b";
  if (s.includes("undeveloped")) return "#2563eb";
  return "#64748b";
}
